import { useState, useEffect, useMemo } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Video from 'yet-another-react-lightbox/plugins/video'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import { shareMedia, prefetchForShare, saveMediaWithPicker, isIOS } from '../utils/mediaHelpers'
import { getFilenameFromUrl } from '../utils/fileHelpers'
import Pressable from './Pressable'

/* ───────────────────────────────────────────────────────────────
 *  GalleryView — full-screen media viewer (multi-photo / video)
 *  Engine: yet-another-react-lightbox (native phone + desktop swipe,
 *  pinch / wheel / double-tap zoom, pan, video, no flash).
 *
 *  IMPORTANT: the library renders in a React PORTAL on document.body,
 *  OUTSIDE this component's DOM. So our styling class MUST be passed to
 *  the Lightbox's own `className` prop (it lands on the portal root) —
 *  wrapping in a <div> does NOT work, the yarl__ elements aren't inside
 *  it. All CSS below is global and scoped to `.airlog-gallery`, which is
 *  that portal root.
 * ─────────────────────────────────────────────────────────────── */
const GALLERY_CONFIG = {
  maxZoom: 5,
  doubleTapDelay: 280,
  zoomInMultiplier: 2,
  wheelZoomFactor: 100,
  pinchZoomFactor: 100,
  scrollToZoom: true,
  fadeMs: 220,
  swipeMs: 300,
  chromeFadeMs: 160,
}

const SFChevronLeft = ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 7 11" fill="currentColor"><path d="M0 5.18555C0.00585938 4.98633 0.0761719 4.81641 0.234375 4.66406L4.80469 0.193359C4.92773 0.0644531 5.0918 0 5.2793 0C5.66016 0 5.95898 0.292969 5.95898 0.673828C5.95898 0.861328 5.88281 1.03125 5.75391 1.16602L1.63477 5.18555L5.75391 9.20508C5.88281 9.33398 5.95898 9.50391 5.95898 9.69141C5.95898 10.0781 5.66016 10.3711 5.2793 10.3711C5.0918 10.3711 4.92773 10.3066 4.80469 10.1777L0.234375 5.70703C0.0761719 5.55469 0 5.38477 0 5.18555Z"/></svg>
const SFShareUp = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 11 14" fill="currentColor"><path d="M5.19727 8.57227C4.94531 8.57227 4.73438 8.36719 4.73438 8.12109V2.15625L4.77539 1.27148L4.44141 1.62891L3.5332 2.5957C3.45117 2.68945 3.32227 2.73633 3.21094 2.73633C2.96484 2.73633 2.78906 2.56055 2.78906 2.32617C2.78906 2.19727 2.8418 2.10352 2.92969 2.01562L4.85742 0.152344C4.98047 0.0351562 5.08008 0 5.19727 0C5.32031 0 5.41992 0.0351562 5.53711 0.152344L7.4707 2.01562C7.55859 2.10352 7.60547 2.19727 7.60547 2.32617C7.60547 2.56055 7.42383 2.73633 7.17773 2.73633C7.06641 2.73633 6.94336 2.68945 6.86133 2.5957L5.95898 1.62891L5.625 1.27734L5.66602 2.15625V8.12109C5.66602 8.36719 5.44922 8.57227 5.19727 8.57227ZM2.44922 13.1602C0.873047 13.1602 0 12.2812 0 10.7109V6.19922C0 4.62305 0.873047 3.75 2.44922 3.75H3.63281V4.69336H2.44922C1.48828 4.69336 0.943359 5.23242 0.943359 6.19922V10.7109C0.943359 11.6777 1.48828 12.2168 2.44922 12.2168H7.94531C8.91211 12.2168 9.45703 11.6777 9.45703 10.7109V6.19922C9.45703 5.23242 8.91211 4.69336 7.94531 4.69336H6.76172V3.75H7.94531C9.52148 3.75 10.4004 4.62305 10.4004 6.19922V10.7109C10.4004 12.2812 9.52148 13.1602 7.94531 13.1602H2.44922Z"/></svg>
const SFSaveDown = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 11 13" fill="currentColor"><path d="M3.21094 5.83594C3.32812 5.83594 3.45117 5.88281 3.5332 5.97656L4.44141 6.94336L4.77539 7.29492L4.73438 6.41016V0.451172C4.73438 0.205078 4.95117 0 5.20312 0C5.45508 0 5.66602 0.205078 5.66602 0.451172V6.41016L5.625 7.29492L5.95898 6.94336L6.86719 5.97656C6.94922 5.88281 7.06641 5.83594 7.18359 5.83594C7.42383 5.83594 7.61133 6.00586 7.61133 6.24609C7.61133 6.375 7.55859 6.46875 7.4707 6.55664L5.53711 8.41992C5.41992 8.53711 5.32031 8.57227 5.20312 8.57227C5.08008 8.57227 4.98047 8.53711 4.86328 8.41992L2.92969 6.55664C2.8418 6.46875 2.78906 6.375 2.78906 6.24609C2.78906 6.00586 2.96484 5.83594 3.21094 5.83594ZM2.44922 12.5273C0.873047 12.5273 0 11.6484 0 10.0781V5.56641C0 3.99023 0.873047 3.11719 2.44922 3.11719H3.63281V4.06055H2.44922C1.48828 4.06055 0.943359 4.60547 0.943359 5.56641V10.0781C0.943359 11.0391 1.48828 11.584 2.44922 11.584H7.94531C8.91211 11.584 9.45703 11.0391 9.45703 10.0781V5.56641C9.45703 4.60547 8.91211 4.06055 7.94531 4.06055H6.76172V3.11719H7.94531C9.52148 3.11719 10.4004 3.99023 10.4004 5.56641V10.0781C10.4004 11.6484 9.52148 12.5273 7.94531 12.5273H2.44922Z"/></svg>
const SFTrashFill = ({ size = 24, color = 'currentColor' }) => <svg width={size} height={size} viewBox="0 0 12 14" fill={color}><path d="M3.08789 13.2246C2.17969 13.2246 1.60547 12.6855 1.56445 11.7832L1.16602 3.33398H0.515625C0.234375 3.33398 0 3.11133 0 2.81836C0 2.53125 0.234375 2.30859 0.515625 2.30859H3.11719V1.42969C3.11719 0.533203 3.69141 0 4.6582 0H6.88477C7.85742 0 8.42578 0.533203 8.42578 1.42969V2.30859H11.0332C11.3262 2.30859 11.5547 2.53125 11.5547 2.81836C11.5547 3.11133 11.3262 3.33398 11.0332 3.33398H10.3887L9.99023 11.7773C9.94922 12.6797 9.36914 13.2246 8.47266 13.2246H3.08789ZM4.19531 1.48242V2.30859H7.34766V1.48242C7.34766 1.17773 7.13672 0.984375 6.80859 0.984375H4.73438C4.40625 0.984375 4.19531 1.17773 4.19531 1.48242ZM3.97852 11.4082C4.23633 11.4082 4.40625 11.2441 4.39453 11.0039L4.21289 4.63477C4.20703 4.38281 4.03125 4.23047 3.79102 4.23047C3.5332 4.23047 3.36914 4.38867 3.375 4.63477L3.55078 11.0098C3.5625 11.2559 3.72656 11.4082 3.97852 11.4082ZM5.77734 11.4082C6.0293 11.4082 6.20508 11.25 6.20508 11.0098V4.63477C6.20508 4.38867 6.0293 4.23047 5.77734 4.23047C5.52539 4.23047 5.34961 4.38867 5.34961 4.63477V11.0098C5.34961 11.25 5.52539 11.4082 5.77734 11.4082ZM7.57617 11.4082C7.82227 11.4082 7.98633 11.2559 7.99805 11.0098L8.17969 4.63477C8.18555 4.38867 8.02148 4.23047 7.76367 4.23047C7.51758 4.23047 7.34766 4.38867 7.3418 4.63477L7.1543 11.0039C7.14844 11.25 7.31836 11.4082 7.57617 11.4082Z"/></svg>
const SFStarFill = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 14 13" fill="currentColor"><path d="M2.60742 12.4863C2.33203 12.2812 2.27344 11.9414 2.43164 11.4785L3.63867 7.89844L0.5625 5.68945C0.164062 5.4082 0 5.09766 0.111328 4.76953C0.222656 4.44727 0.527344 4.29492 1.01367 4.29492H4.78711L5.93555 0.720703C6.08789 0.251953 6.32812 0 6.66797 0C7.01367 0 7.25391 0.251953 7.40039 0.720703L8.54883 4.29492H12.3223C12.8086 4.29492 13.1191 4.44727 13.2305 4.76953C13.3418 5.09766 13.1719 5.4082 12.7793 5.68945L9.70312 7.89844L10.9043 11.4785C11.0625 11.9414 11.0039 12.2812 10.7344 12.4863C10.459 12.6973 10.1133 12.627 9.71484 12.3398L6.66797 10.1016L3.62109 12.3398C3.22266 12.627 2.88281 12.6973 2.60742 12.4863Z"/></svg>
const SFStar = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 14 13" fill="currentColor"><path d="M2.60742 12.4863C2.33203 12.2812 2.27344 11.9414 2.43164 11.4785L3.63867 7.89844L0.5625 5.68945C0.164062 5.4082 0 5.09766 0.111328 4.76953C0.222656 4.44727 0.527344 4.28906 1.01367 4.29492L4.78711 4.31836L5.93555 0.720703C6.08789 0.251953 6.32812 0 6.66797 0C7.01367 0 7.25391 0.251953 7.40039 0.720703L8.54883 4.31836L12.3223 4.29492C12.8086 4.28906 13.1191 4.44727 13.2305 4.76953C13.3418 5.09766 13.1719 5.4082 12.7793 5.68945L9.70312 7.89844L10.9043 11.4785C11.0625 11.9414 11.0039 12.2812 10.7344 12.4863C10.459 12.6973 10.1133 12.627 9.71484 12.3398L6.66797 10.1016L3.62109 12.3398C3.22266 12.627 2.88281 12.6973 2.60742 12.4863ZM3.63281 11.0742C3.64453 11.0918 3.66211 11.0801 3.68555 11.0684L6.32812 9.0293C6.5625 8.8418 6.77344 8.8418 7.01367 9.0293L9.65625 11.0684C9.67969 11.0801 9.69141 11.0918 9.70312 11.0742C9.71484 11.0625 9.71484 11.0566 9.70312 11.0273L8.58984 7.88086C8.49023 7.59375 8.52539 7.41797 8.79492 7.23633L11.543 5.34961C11.5723 5.33203 11.5781 5.32617 11.5723 5.30859C11.5664 5.29688 11.5547 5.29102 11.5195 5.29102L8.18555 5.36719C7.88672 5.37891 7.72852 5.2793 7.64062 4.98633L6.70312 1.78711C6.69141 1.75195 6.68555 1.74609 6.66797 1.74609C6.65625 1.74609 6.64453 1.75195 6.63867 1.78711L5.69531 4.98633C5.60742 5.2793 5.45508 5.37891 5.15039 5.36719L1.81641 5.29102C1.78711 5.29102 1.77539 5.29688 1.76367 5.30859C1.75781 5.32617 1.76953 5.33789 1.79297 5.34961L4.54688 7.23633C4.81055 7.41211 4.8457 7.59375 4.74609 7.88086L3.63281 11.0273C3.62695 11.0566 3.62695 11.0625 3.63281 11.0742Z"/></svg>

/* Small triangle (rotated 90°) used as the play badge on video thumbnails. */
const SFTrianglePlay = ({ size = 11 }) => <svg width={size} height={size} viewBox="0 0 13 12" fill="none" style={{ transform: 'rotate(90deg)' }}><path d="M1.66406 11.2441C0.638672 11.2441 0 10.5059 0 9.58594C0 9.30469 0.0761719 9.01758 0.234375 8.74219L4.76953 0.832031C5.08008 0.28125 5.63086 0 6.19336 0C6.75 0 7.30078 0.28125 7.61719 0.832031L12.1523 8.74219C12.3047 9.01172 12.3867 9.30469 12.3867 9.58594C12.3867 10.5059 11.7422 11.2441 10.7227 11.2441H1.66406Z" fill="white"/></svg>

const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v|ogg|ogv)(\?|$)/i.test(url || '')

const isUrlStarred = (log, url) => {
  if (!log) return false
  if (log.is_starred) return true
  return (log.starred_urls || []).includes(url)
}

/* Pill (65×44) and circle (44×44) — matching the original AirLog UI exactly. */
const glassPill = {
  width: '65px', height: '44px', borderRadius: '100px', padding: 0,
  background: 'rgba(255,255,255,0.2)',
  backdropFilter: 'saturate(180%) blur(20px)',
  WebkitBackdropFilter: 'saturate(180%) blur(20px)',
  border: '0.5px solid rgba(255,255,255,0.39)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff', WebkitTapHighlightColor: 'transparent',
}
const glassCircle = { ...glassPill, width: '44px', borderRadius: '50%' }

function GalleryView({ data, onClose, onToggleStar, onToggleStarUrl, onToggleStarUrls, onDelete, logs, fetchLogs }) {
  const [index, setIndex] = useState(data?.index ?? 0)
  const [chromeHidden, setChromeHidden] = useState(false)

  useEffect(() => { setIndex(data?.index ?? 0) }, [data?.index])
  useEffect(() => { setChromeHidden(false) }, [data?.index])

  const slides = useMemo(() => {
    if (!data?.urls) return []
    return data.urls.map((url) =>
      isVideoUrl(url) ? { type: 'video', sources: [{ src: url, type: 'video/mp4' }] } : { src: url }
    )
  }, [data?.urls])

  useEffect(() => {
    const url = data?.urls?.[index]
    if (url) prefetchForShare(url, getFilenameFromUrl(url))
  }, [index, data?.urls])

  if (!data) return null

  const currentUrl = data.urls[index]
  const currentLog = data.logsMap ? data.logsMap[index] : data.log
  const currentUrlStarred = isUrlStarred(currentLog, currentUrl)
  const showSaveButton = !isIOS()

  const handleStarTap = () => { if (currentLog && currentUrl && onToggleStarUrl) onToggleStarUrl(currentLog.id, currentUrl) }
  const handleDelete = () => onDelete([currentUrl], data)
  const handleShare = () => shareMedia(currentUrl, getFilenameFromUrl(currentUrl))
  const handleSave = () => saveMediaWithPicker(currentUrl, getFilenameFromUrl(currentUrl))

  return (
    <>
      {/* GLOBAL styles, scoped to the portal root class `.airlog-gallery`
          (passed to the Lightbox below via className). */}
      <style>{`
        /* Cover the app's NavBar (and everything). The portal element holds
           the z-index (defaults to 9999); AirLog's liquid-glass NavBar floats
           above that and bled through. Only one lightbox is open at a time, so
           targeting .yarl__portal globally is safe. */
        .yarl__portal { z-index: 2147483000 !important; }

        .airlog-gallery .yarl__navigation_prev,
        .airlog-gallery .yarl__navigation_next { display: none !important; }

        /* hide the zoom +/- magnifier buttons (zoom still works via pinch /
           scroll / double-tap). */
        .airlog-gallery .yarl__toolbar button[aria-label="Zoom in"],
        .airlog-gallery .yarl__toolbar button[aria-label="Zoom out"],
        .airlog-gallery .yarl__toolbar button[title="Zoom in"],
        .airlog-gallery .yarl__toolbar button[title="Zoom out"] { display: none !important; }

        .airlog-gallery .yarl__toolbar { padding: 16px !important; gap: 8px !important; }

        /* clean thumbnail strip — SOLID dark bar so the NavBar can't show
           through, and strip the library's default rounded-frame decorations. */
        .airlog-gallery .yarl__thumbnails_container {
          background: #000 !important;
          padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
          --yarl__thumbnails_thumbnail_background: transparent;
        }
        .airlog-gallery .yarl__thumbnails_track { gap: 6px !important; }
        .airlog-gallery .yarl__thumbnails_thumbnail {
          width: 52px !important; height: 52px !important;
          border-radius: 8px !important;
          border: 2px solid transparent !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
          background: rgba(255,255,255,0.06) !important;
          box-shadow: none !important;
          opacity: 0.55; transition: opacity 0.2s, border-color 0.2s;
        }
        .airlog-gallery .yarl__thumbnails_thumbnail img,
        .airlog-gallery .yarl__thumbnails_thumbnail video {
          border-radius: 6px !important;
        }
        .airlog-gallery .yarl__thumbnails_thumbnail_active {
          border-color: var(--color-primary, #007AFF) !important; opacity: 1;
        }

        /* TAP-TO-HIDE: fade chrome when .chrome-hidden is on the root.
           The thumbnail CONTAINER is fully removed (display:none) when hidden
           — NOT faded to transparent. Fading it transparent left the strip's
           space in the layout and revealed the carousel slide behind it (the
           sliver bug). display:none reflows the carousel to full height, so
           the photo fills the screen cleanly like the single-media Lightbox. */
        .airlog-gallery .yarl__toolbar,
        .airlog-gallery .airlog-controls {
          transition: opacity ${GALLERY_CONFIG.chromeFadeMs}ms cubic-bezier(0.4,0,0.2,1);
        }
        .airlog-gallery.chrome-hidden .yarl__toolbar,
        .airlog-gallery.chrome-hidden .airlog-controls {
          opacity: 0 !important; pointer-events: none !important;
        }
        .airlog-gallery.chrome-hidden .yarl__thumbnails_container {
          display: none !important;
        }
      `}</style>

      <Lightbox
        className={chromeHidden ? 'airlog-gallery chrome-hidden' : 'airlog-gallery'}
        open={!!data}
        close={onClose}
        index={index}
        on={{
          view: ({ index: i }) => setIndex(i),
        }}
        slides={slides}
        plugins={[Zoom, Video, Thumbnails]}
        carousel={{ finite: true, preload: 2, padding: 0, spacing: 0, imageFit: 'contain' }}
        animation={{ fade: GALLERY_CONFIG.fadeMs, swipe: GALLERY_CONFIG.swipeMs }}
        controller={{ closeOnBackdropClick: false }}
        zoom={{
          maxZoomPixelRatio: GALLERY_CONFIG.maxZoom,
          zoomInMultiplier: GALLERY_CONFIG.zoomInMultiplier,
          doubleTapDelay: GALLERY_CONFIG.doubleTapDelay,
          wheelZoomDistanceFactor: GALLERY_CONFIG.wheelZoomFactor,
          pinchZoomDistanceFactor: GALLERY_CONFIG.pinchZoomFactor,
          scrollToZoom: GALLERY_CONFIG.scrollToZoom,
        }}
        thumbnails={{ position: 'bottom', width: 52, height: 52, border: 0, gap: 6, padding: 0, imageFit: 'cover', showToggle: false }}
        toolbar={{
          buttons: [
            <Pressable key="star" onPress={handleStarTap} style={glassPill}>
              {currentUrlStarred ? <SFStarFill size={24} /> : <SFStar size={24} />}
            </Pressable>,
            <Pressable key="trash" onPress={handleDelete} style={{ ...glassPill, color: 'var(--color-danger)' }}>
              <SFTrashFill size={24} color="var(--color-danger)" />
            </Pressable>,
          ],
        }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
          buttonClose: () => null,
          /* TAP-TO-TOGGLE that works ANYWHERE on the slide (including the
             black letterbox), unlike the library's on.click which only fires
             on the image itself. We wrap each slide and detect a clean tap
             (pointer down→up with no real movement), so it never fights swipe
             or zoom. */
          slideContainer: ({ slide, children }) => {
            const down = { x: 0, y: 0, moved: false }
            return (
              <div
                style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onPointerDown={(e) => { down.x = e.clientX; down.y = e.clientY; down.moved = false }}
                onPointerMove={(e) => { if (Math.abs(e.clientX - down.x) > 8 || Math.abs(e.clientY - down.y) > 8) down.moved = true }}
                onPointerUp={(e) => {
                  if (e.target.closest && e.target.closest('button')) return
                  if (!down.moved) setChromeHidden((h) => !h)
                }}
              >
                {children}
              </div>
            )
          },
          /* Video thumbnails: show the video frame (like a photo) + a small
             triangle play badge in the corner, instead of the library's big
             default play circle. */
          thumbnail: ({ slide }) => {
            const isVid = slide.type === 'video'
            const vidSrc = isVid ? (slide.sources && slide.sources[0] && slide.sources[0].src) : null
            const imgSrc = !isVid ? slide.src : null
            return (
              <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                {isVid ? (
                  <video src={vidSrc} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
                {isVid && (
                  <div style={{ position: 'absolute', bottom: '4px', right: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.7, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>
                    <SFTrianglePlay size={6} />
                  </div>
                )}
              </div>
            )
          },
          controls: () => (
            <>
              <div className="airlog-controls" style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 5, pointerEvents: 'auto' }}>
                <Pressable onPress={onClose} style={glassCircle}><SFChevronLeft size={22} /></Pressable>
              </div>
              <div className="airlog-controls" style={{ position: 'absolute', bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))', left: 0, right: 0, padding: '0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 5, pointerEvents: 'none' }}>
                <div style={{ pointerEvents: 'auto' }}>
                  {showSaveButton && <Pressable onPress={handleSave} style={glassPill}><SFSaveDown size={24} /></Pressable>}
                </div>
                <div style={{ pointerEvents: 'auto' }}>
                  <Pressable onPress={handleShare} style={glassPill}><SFShareUp size={24} /></Pressable>
                </div>
              </div>
            </>
          ),
        }}
        styles={{ container: { backgroundColor: '#000' } }}
      />
    </>
  )
}

export default GalleryView